import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Gem, Award, Users, Truck, ShieldCheck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const values = [
    {
      icon: Crown,
      title: 'Uncompromising Quality',
      description: 'Every piece in our collection meets the highest standards of craftsmanship and authenticity.'
    },
    {
      icon: Gem,
      title: 'Exclusive Selection',
      description: 'We curate only the most exceptional and rare collectibles for our discerning clientele.'
    },
    {
      icon: Award,
      title: 'Authenticated Luxury',
      description: 'Each item comes with verification of authenticity and provenance documentation.'
    },
    {
      icon: Users,
      title: 'Elite Community',
      description: 'Join a distinguished circle of collectors who share your passion for excellence.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section */}
      <section className="relative bg-[#0A0A0A] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 to-transparent" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37]/20 rounded-full text-sm font-medium mb-6 font-body border border-[#D4AF37]/50">
              <Crown className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] tracking-widest uppercase">Our Story</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold mb-6 font-heading">
              The Art of <span className="text-[#D4AF37]">Collecting</span>
            </h1>
            <p className="text-xl lg:text-2xl text-[#888888] font-body leading-relaxed">
              BOXVERRA represents the pinnacle of luxury collectibles. 
              We curate extraordinary pieces for those who appreciate the finest things in life.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 lg:py-28 bg-[#141414]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#D4AF37] font-semibold font-body tracking-widest uppercase">Who We Are</span>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mt-2 mb-6 font-heading">
                A Legacy of Excellence
              </h2>
              <div className="space-y-4 text-lg text-[#888888] font-body leading-relaxed">
                <p>
                  Founded by collectors, for collectors. BOXVERRA emerged from a simple belief: 
                  that exceptional items deserve exceptional presentation and service.
                </p>
                <p>
                  Our curators travel the globe, forging relationships with the world's most prestigious 
                  artisans and brands to bring you pieces that transcend ordinary collecting.
                </p>
                <p>
                  Every item in our collection tells a story. From limited-edition designer toys to 
                  rare vintage finds, we offer only pieces that meet our exacting standards of quality 
                  and authenticity.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=800"
                alt="Luxury interior"
                className="rounded-2xl shadow-2xl shadow-[#D4AF37]/20 border border-[#D4AF37]/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 lg:py-28 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] font-semibold font-body tracking-widest uppercase">Our Values</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mt-2 font-heading">
              What Sets Us Apart
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-[#141414] rounded-2xl p-8 border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors">
                  <div className="w-14 h-14 bg-[#D4AF37]/20 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="h-7 w-7 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 font-heading">{value.title}</h3>
                  <p className="text-[#888888] font-body">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-[#141414] py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold font-heading text-white">The BOXVERRA Experience</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/30">
                <Truck className="h-8 w-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-heading text-white">White-Glove Delivery</h3>
              <p className="text-[#888888] font-body">Premium packaging and insured shipping on every order.</p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/30">
                <ShieldCheck className="h-8 w-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-heading text-white">Authenticity Guaranteed</h3>
              <p className="text-[#888888] font-body">Every item verified and certified for your peace of mind.</p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/30">
                <Crown className="h-8 w-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-bold mb-3 font-heading text-white">VIP Concierge</h3>
              <p className="text-[#888888] font-body">Dedicated support for sourcing rare and exclusive pieces.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37] py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0A0A0A] mb-6 font-heading">
            Begin Your Collection
          </h2>
          <p className="text-xl text-[#0A0A0A]/80 mb-8 max-w-2xl mx-auto font-body">
            Discover extraordinary pieces that reflect your exceptional taste.
          </p>
          <Link to="/shop">
            <Button className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#1A1A1A] text-lg px-10 py-6 font-body font-bold rounded-full">
              Explore Collection
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
